import React, { ChangeEvent, useEffect, useState } from 'react';

import { IProductTemplateAttribute } from 'components/dashboard/product-template-table/utils/interfaces';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { CellType } from 'components/dashboard/product-template-table/utils/enums';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch, InputSwitchChangeParams } from 'primereact/inputswitch';
import ColorPicker from 'components/basic/color-picker/ColorPicker';
import { Divider } from 'primereact/divider';
import LongAndDoubleTypes from './type-fields/LongAndDoubleTypes';
import StringType from './type-fields/StringType';
import DatetimeAndInstantTypes from './type-fields/DatetimeAndInstantTypes';
import { Tooltip } from 'react-tooltip';
import { Checkbox, CheckboxChangeParams } from 'primereact/checkbox';

type ProductTemplateAttributeDialogProps = {
	attribute: IProductTemplateAttribute;
	index: number;
	onAttributeChange: (
		newAttribute: IProductTemplateAttribute,
		index: number,
	) => void;
};

const ProductTemplateAttributeDialog: React.FC<
	ProductTemplateAttributeDialogProps
> = ({ attribute, index, onAttributeChange }) => {
	const [attr, setAttr] = useState(attribute);
	const [dialog, setDialog] = useState(false);
	const [editTextColors, setEditTextColors] = useState(false);
	const [editBgColors, setEditBgColors] = useState(false);

	useEffect(() => {
		setAttr(attribute);
		setEditTextColors(!!attribute.highlightColor?.color);
		setEditBgColors(!!attribute.highlightColor?.bgColor);
	}, [attribute]);

	const cancelDialog = () => {
		setAttr(attribute);
		setDialog(false);
	};

	const saveDialog = () => {
		onAttributeChange(attr, index);
		setDialog(false);
	};

	const onFieldChange = (
		e:
			| DropdownChangeParams
			| ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => setAttr(prev => ({ ...prev, [e.target.name]: e.target.value }));

	const onEditColor = (e: CheckboxChangeParams, field: 'color' | 'bgColor') => {
		field === 'color'
			? setEditTextColors(e.checked)
			: setEditBgColors(e.checked);

		if (e.checked) {
			setAttr(prev => ({
				...prev,
				highlightColor: {
					...prev.highlightColor,
					[field]: field === 'color' ? '#000000' : '#ffffff',
				},
			}));
		} else {
			const { highlightColor, ...restAttr } = attr;
			const { [field]: extracted, ...restHighlightColor } = highlightColor!;
			highlightColor?.[field]
				? setAttr({
						...restAttr,
						highlightColor: restHighlightColor,
				  })
				: setAttr({ ...restAttr });
		}
	};

	const onColorChange = (e: ChangeEvent<HTMLInputElement>) =>
		setAttr(prev => ({
			...prev,
			highlightColor: prev.highlightColor
				? { ...prev.highlightColor, [e.target.name]: e.target.value }
				: { [e.target.name]: e.target.value },
		}));

	const dialogFooterContent = () => {
		return (
			<div>
				<Button
					label='Cancel'
					icon='pi pi-times'
					onClick={cancelDialog}
					className='p-button-sm p-button-danger'
				/>
				<Button
					label='Save'
					icon='pi pi-check'
					onClick={saveDialog}
					className='p-button-sm p-button-success'
				/>
			</div>
		);
	};

	return (
		<>
			<Tooltip id='edit-btn' />
			<Button
				data-tooltip-id='edit-btn'
				data-tooltip-content='Edit'
				icon={'pi pi-cog'}
				className={'p-button-sm'}
				onClick={() => setDialog(true)}
			/>
			<Dialog
				blockScroll
				visible={dialog}
				onHide={() => setDialog(false)}
				header={`"${attr.displayName || 'No Name'}" Column Configuration`}
				style={{ width: 'min(100%, 1000px)' }}
				footer={dialogFooterContent}>
				<div className='card-class'>
					<div className='p-fluid p-formgrid p-grid'>
						<div className='p-field p-col-4'>
							<label>Type</label>
							<Dropdown
								options={Object.keys(CellType)}
								value={attr.type ?? CellType.STRING}
								onChange={onFieldChange}
								name='type'
								className='p-inputtext-sm'
							/>
						</div>
						<div className='p-field p-col-4'>
							<label>Display name</label>
							<InputText
								placeholder='Display name'
								value={attr.displayName}
								onChange={onFieldChange}
								name='displayName'
								className='p-inputtext-sm'
								required
							/>
						</div>
						<div className='p-field p-col-4'>
							<label>Reference name</label>
							<InputText
								placeholder='Reference name'
								value={attr.referenceName}
								onChange={onFieldChange}
								name='referenceName'
								className='p-inputtext-sm'
								required
							/>
						</div>
						<div className='p-field p-col-12'>
							<label>Description (optional)</label>
							<InputTextarea
								placeholder='Description'
								value={attr.description}
								onChange={onFieldChange}
								name='description'
								className='p-inputtext-sm'
								style={{ resize: 'vertical', minHeight: '4.4em' }}
							/>
						</div>

						<Divider />

						<div className='flex w-full gap-3 m-3 h-2rem'>
							<div className='flex-1 flex'>
								<div className='flex align-items-center gap-3 px-3'>
									<label>Set custom text color:</label>
									<Checkbox
										checked={editTextColors}
										onChange={e => onEditColor(e, 'color')}
										className='p-inputtext-sm mb-2'
									/>
								</div>
								{editTextColors && (
									<div className='flex align-items-center gap-3 px-3'>
										<label>Text color:</label>
										<ColorPicker
											value={attr.highlightColor?.color}
											onChange={onColorChange}
											name='color'
											className='p-inputtext-sm mb-2'
										/>
									</div>
								)}
							</div>

							<div className='flex-1 flex'>
								<div className='flex align-items-center gap-3 px-3'>
									<label>Set custom text color:</label>
									<Checkbox
										checked={editBgColors}
										onChange={e => onEditColor(e, 'bgColor')}
										className='p-inputtext-sm mb-2'
									/>
								</div>
								{editBgColors && (
									<div className='flex align-items-center gap-3 px-3'>
										<label>Background color:</label>
										<ColorPicker
											value={attr.highlightColor?.bgColor}
											onChange={onColorChange}
											name='bgColor'
											className='p-inputtext-sm'
										/>
									</div>
								)}
							</div>
						</div>

						<Divider />
						<div className='p-field p-col-6'>
							<label>Required permission</label>
							<InputText
								placeholder='Required permission to edit this column'
								value={attr.requiredPermission}
								onChange={onFieldChange}
								name='requiredPermission'
								className='p-inputtext-sm'
							/>
						</div>
						<div className='p-col-6 flex align-self-center justify-content-center gap-3'>
							<label>Locked</label>
							<InputSwitch
								checked={attr.locked ?? false}
								onChange={onFieldChange}
								name='locked'
								className='p-inputswitch-sm'
							/>
						</div>

						<Divider />
						<OptionalFields
							attribute={attr}
							type={attr.type}
							onAttributeChange={setAttr}
						/>
					</div>
				</div>
			</Dialog>
		</>
	);
};

type OptionalFieldsProps = {
	attribute: IProductTemplateAttribute;
	type: CellType;
	onAttributeChange: React.Dispatch<
		React.SetStateAction<IProductTemplateAttribute>
	>;
};
const OptionalFields: React.FC<OptionalFieldsProps> = ({
	attribute,
	type,
	onAttributeChange,
}) => {
	const onFieldChange = (
		e: ChangeEvent<HTMLInputElement> | InputSwitchChangeParams,
	) =>
		onAttributeChange(prev => ({
			...prev,
			[e.target.name]: e.target.value,
		}));

	switch (type) {
		case CellType.LONG:
		case CellType.DOUBLE:
			return (
				<LongAndDoubleTypes
					attribute={attribute}
					onAttrChangeInLongAndDouble={onAttributeChange}
				/>
			);
		case CellType.STRING:
			return (
				<StringType
					attribute={attribute}
					onAttrChangeInString={onAttributeChange}
				/>
			);
		case CellType.DATETIME:
		case CellType.INSTANT:
			return (
				<DatetimeAndInstantTypes
					attribute={attribute}
					onAttrChangeInDatetimeAndInstant={onAttributeChange}
				/>
			);

		case CellType.FOREIGN_KEY:
			return (
				<div className='p-field p-col-6'>
					<label>Foreign template</label>
					<InputText
						placeholder='Foreign template'
						value={attribute.foreignTemplate}
						onChange={onFieldChange}
						name='foreignTemplate'
						className='p-inputtext-sm'
					/>
				</div>
			);
		case CellType.TIME:
			return (
				<div className='flex-1 flex w-full justify-content-start gap-3 p-3'>
					<label htmlFor='autoFillTime'>Auto fill time</label>
					<InputSwitch
						id='autoFillTime'
						checked={attribute.autoFillTime ?? false}
						onChange={onFieldChange}
						name='autoFillTime'
						className='p-inputtext-sm'
					/>
				</div>
			);
		case CellType.TIMESPAN:
		case CellType.USER_SIGNATURE:
		default:
			return <></>;
	}
};

export default ProductTemplateAttributeDialog;
